/**
 * LanguageSelector
 *
 * Settings row for choosing the app's language, laid out to match the Quiet
 * Mode row above it.
 *
 * Offers an explicit "Device setting" option alongside the concrete languages,
 * so a user can hand control back to the phone after overriding it — with only
 * English and Español buttons there would be no way to undo a choice.
 *
 * Language names are written in their own language (English, Español) rather
 * than translated, which is the convention users expect from a language picker:
 * someone who has the app stuck in a language they cannot read still needs to
 * find their own.
 */

import React, { useEffect, useState } from 'react';
import { View, Pressable, StyleSheet } from 'react-native';

import AppStyles from '../AppStyles';
import { SYSTEM_LANGUAGE, setAppLanguage, useTranslation } from '../i18n';
import { getLanguage } from '../util/storage';

import CustomText from './CustomText';

/** Endonyms — deliberately not translation keys. See the note above. */
const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
];

const LanguageSelector = () => {
  const { t } = useTranslation(['settings', 'common']);
  const [selected, setSelected] = useState(SYSTEM_LANGUAGE);

  useEffect(() => {
    let active = true;
    getLanguage().then((stored) => {
      if (active) setSelected(stored);
    });
    return () => {
      active = false;
    };
  }, []);

  const choose = async (value) => {
    // Optimistic: the press should feel instant, and setAppLanguage only fails
    // if AsyncStorage does — in which case the language still switches for this
    // session and simply is not remembered.
    setSelected(value);
    await setAppLanguage(value);
  };

  const options = [
    { value: SYSTEM_LANGUAGE, label: t('settings:language.system') },
    ...LANGUAGE_OPTIONS,
  ];

  return (
    <View style={styles.row}>
      <View style={styles.text}>
        <CustomText font="medium" size={14} color={AppStyles.colorSet.textPrimary}>
          {t('settings:language.title')}
        </CustomText>
        <CustomText font="regular" size={12} color={AppStyles.colorSet.textSecondary}>
          {t('settings:language.description')}
        </CustomText>
      </View>

      <View style={styles.options}>
        {options.map((option) => {
          const isSelected = option.value === selected;
          return (
            <Pressable
              key={option.value}
              onPress={() => choose(option.value)}
              accessibilityRole="radio"
              accessibilityState={{ selected: isSelected }}
              accessibilityLabel={option.label}
              testID={`language-option-${option.value}`}
              style={[styles.option, isSelected && styles.optionSelected]}
            >
              <CustomText
                font={isSelected ? 'semiBold' : 'regular'}
                size={12}
                color={isSelected ? AppStyles.colorSet.textPrimary : AppStyles.colorSet.textSecondary}
              >
                {option.label}
              </CustomText>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  row: {
    paddingVertical: 12,
  },
  text: {
    marginBottom: 10,
  },
  options: {
    flexDirection: 'row',
    // Wrap rather than shrink: Spanish and any future language names run longer
    // than English, and a squeezed row would clip them.
    flexWrap: 'wrap',
    gap: 8,
  },
  option: {
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: AppStyles.colorSet.inputBorder,
    backgroundColor: AppStyles.colorSet.cardBackground,
  },
  optionSelected: {
    borderColor: AppStyles.colorSet.brandPrimary,
    backgroundColor: AppStyles.colorSet.brandPrimary,
  },
});

export default LanguageSelector;
